import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { MessageSquare, FileCheck, Code, Rocket, HeartHandshake } from 'lucide-react';

const HowItWorks = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const steps = [
    {
      icon: MessageSquare,
      title: "Free Consultation",
      description: "We discuss your vision, requirements, and goals. No obligation, just honest advice.",
    },
    {
      icon: FileCheck,
      title: "Agreement & 50% Deposit",
      description: "Once we're aligned, you pay 50% to kick off the project. Clear scope, no surprises.",
    },
    {
      icon: Code,
      title: "Development",
      description: "We build your solution with 100% custom code. Regular updates keep you informed.",
    },
    {
      icon: HeartHandshake,
      title: "Review & Refine",
      description: "You review the work, we make adjustments until you're completely satisfied.",
    },
    {
      icon: Rocket,
      title: "Launch & Support",
      description: "We launch your project and provide 1 year of free maintenance and support.",
    }
  ];

  return (
    <section className="py-20 bg-white dark:bg-gray-900">
      <div className="max-w-6xl mx-auto px-4">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            How It <span className="text-primary-600">Works</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Our simple 5-step process ensures transparency and delivers results you'll love.
          </p>
        </motion.div>

        {/* Desktop Timeline */}
        <div className="hidden lg:block relative">
          {/* Connection Line */}
          <div className="absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-primary-500 via-cyan-500 to-primary-500 rounded-full"></div>

          <div className="grid grid-cols-5 gap-4">
            {steps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="relative text-center"
              >
                {/* Step Number */}
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 w-8 h-8 bg-white dark:bg-gray-900 rounded-full flex items-center justify-center border-2 border-primary-500 text-primary-600 font-bold text-sm z-10">
                  {index + 1}
                </div>

                {/* Icon */}
                <div className="w-16 h-16 mx-auto mb-4 mt-8 rounded-2xl bg-gray-800 dark:bg-gray-700 flex items-center justify-center shadow-lg border border-gray-700 dark:border-gray-600">
                  <step.icon className="w-8 h-8 text-primary-400" />
                </div>

                {/* Content */}
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                  {step.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Mobile/Tablet Timeline */}
        <div className="lg:hidden space-y-6">
          {steps.map((step, index) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, x: -30 }}
              animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="flex gap-4 items-start"
            >
              {/* Step indicator */}
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 rounded-xl bg-gray-800 dark:bg-gray-700 flex items-center justify-center shadow-lg flex-shrink-0 border border-gray-700 dark:border-gray-600">
                  <step.icon className="w-6 h-6 text-primary-400" />
                </div>
                {index < steps.length - 1 && (
                  <div className="w-0.5 h-16 bg-gray-200 dark:bg-gray-700 mt-2"></div>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 pb-8">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-bold text-primary-600 bg-primary-100 dark:bg-primary-900/30 px-2 py-0.5 rounded">
                    Step {index + 1}
                  </span>
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">
                  {step.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {step.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;

